/* eslint-disable react/no-danger */
/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FiBriefcase,
  FiCalendar,
  FiCheckCircle,
  FiClipboard,
  FiCoffee,
  FiFileText,
  FiInfo,
  FiMap,
  FiMapPin,
  FiMessageSquare,
  FiMoon,
  FiPlayCircle,
  FiSend,
  FiShield,
  FiStar,
  FiTag,
  FiUsers,
  FiXCircle,
} from 'react-icons/fi';
import { MdOutlineHotel } from 'react-icons/md';
import PackageSummaryBar from '../../components/common/PackageSummaryBar';
import PackageState from '../../components/common/PackageState';
import PackageGallery from '../../components/PackageDetails/PackageGallery';
import PackageItinerary from '../../components/PackageDetails/PackageItinerary';
import {
  PackageFlights,
  PackageHotels,
} from '../../components/PackageDetails/PackageStay';
import PackageReviews from '../../components/PackageDetails/PackageReviews';
import PackageVideos from '../../components/PackageDetails/PackageVideos';
import PackageBookingPanel from '../../components/PackageDetails/PackageBookingPanel';
import PackageEnquiryModal from '../../components/PackageEnquiryModal/PackageEnquiryModal';
import { packageDetailApi, packageQuoteApi } from '../../api/packagesApi';
import { PACKAGE_ROUTES } from '../../constants/packageConstants';
import {
  apiPax,
  buildPackageSearchQuery,
  daysNightsText,
  estimatePackageTotal,
  flightsForCity,
  formatDate,
  formatPrice,
  gradeLabel,
  hotelsForGrade,
  normalizePax,
  packageDays,
  packageDepartures,
  packageGallery,
  packageImageUrl,
  packageItinerary,
  packageNights,
  packagePlace,
  packagePriceOptions,
  packageReviews,
  packageSections,
  packageSupplier,
  packageVideos,
  parsePackageSearchQuery,
  paxError,
  paxForOption,
  paxSummary,
  prettyDestination,
  sectionTone,
  seatsOf,
  toIsoDate,
} from '../../utils/packageUtils';
import { sanitizeHtml } from '../../utils/safeHtml';
import { savePackageBookContext } from '../../utils/packageBookContext';
import '../../styles/packagesShared.scss';
import './PackageDetailsPage.scss';

const NAV = [
  { key: 'overview', label: 'Overview', icon: FiFileText },
  { key: 'itinerary', label: 'Itinerary', icon: FiMap },
  { key: 'stay', label: 'Hotels', icon: MdOutlineHotel },
  { key: 'flights', label: 'Flights', icon: FiSend },
  { key: 'inclusions', label: 'Inclusions', icon: FiClipboard },
  { key: 'videos', label: 'Videos', icon: FiPlayCircle },
  { key: 'reviews', label: 'Reviews', icon: FiStar },
  { key: 'policies', label: 'Policies', icon: FiShield },
];
const NAV_ICON = Object.fromEntries(NAV.map((n) => [n.key, n.icon]));
const TONE_ICON = { in: FiCheckCircle, out: FiXCircle, info: FiInfo };

/** Which section is on screen, for the sticky section nav. */
function useActiveSection(keys) {
  const [active, setActive] = useState(keys[0] || '');
  // keyed on the joined ids: a new array with the same sections is no change
  const signature = keys.join('|');
  useEffect(() => {
    const ids = signature ? signature.split('|') : [];
    if (!ids.length || typeof IntersectionObserver === 'undefined')
      return undefined;
    const nodes = ids
      .map((k) => document.getElementById(`pkg-section-${k}`))
      .filter(Boolean);
    const visible = new Map();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => visible.set(e.target.id, e.isIntersecting));
        const first = nodes.find((n) => visible.get(n.id));
        if (first) setActive(first.id.replace('pkg-section-', ''));
      },
      { rootMargin: '-90px 0px -55% 0px' },
    );
    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, [signature]);
  return [active, setActive];
}

function Section({ id, title, aside, children }) {
  const Icon = NAV_ICON[id];
  return (
    <section className="pkg-section" id={`pkg-section-${id}`}>
      <div className="pkg-section__head">
        <h2 className="pkg-section__title">
          {Icon && (
            <span className="pkg-section__icon" aria-hidden>
              <Icon />
            </span>
          )}
          {title}
        </h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

function Fact({ icon, label, value, sub, accent }) {
  return (
    <div className={`pkg-fact ${accent ? 'pkg-fact--accent' : ''}`}>
      <span className="pkg-fact__icon" aria-hidden>
        {icon}
      </span>
      <span className="pkg-fact__text">
        <small>{label}</small>
        <strong>{value}</strong>
        {sub && <em>{sub}</em>}
      </span>
    </div>
  );
}

function DetailsSkeleton() {
  return (
    <Container className="pkg-details__main" aria-busy="true">
      <span className="pkg-skeleton pkg-details__skeleton-gallery" />
      <div className="pkg-details__grid">
        <div>
          <span className="pkg-skeleton pkg-skeleton--title" />
          <span className="pkg-skeleton" />
          <span className="pkg-skeleton pkg-skeleton--block" />
        </div>
        <span className="pkg-skeleton pkg-skeleton--block" />
      </div>
    </Container>
  );
}

function PackageDetailsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const packageKey = params.get('key') || '';
  const searchId = params.get('sid') || '';
  const search = useMemo(
    () => parsePackageSearchQuery(location.search),
    [location.search],
  );

  const [state, setState] = useState({ loading: true, read: null, pkg: null });
  const [attempt, setAttempt] = useState(0);
  const [selection, setSelection] = useState(() => ({
    departureKey: '',
    priceKey: '',
    pax: normalizePax(search.pax),
  }));
  const [quoting, setQuoting] = useState(false);
  const [quoteError, setQuoteError] = useState('');
  const [enquiryOpen, setEnquiryOpen] = useState(false);

  useEffect(() => {
    if (!packageKey) return undefined;
    const controller = new AbortController();
    setState((s) => ({ ...s, loading: true }));
    packageDetailApi({ packageKey, searchId }, controller.signal).then(
      (read) => {
        if (controller.signal.aborted) return;
        setState({
          loading: false,
          read,
          pkg: read.ok ? read.data?.package || null : null,
        });
      },
    );
    return () => controller.abort();
  }, [packageKey, searchId, attempt]);

  const { pkg } = state;
  const seats = seatsOf(selection.pax);
  const departures = useMemo(
    () => (pkg ? packageDepartures(pkg, seats) : []),
    [pkg, seats],
  );
  const options = useMemo(() => (pkg ? packagePriceOptions(pkg) : []), [pkg]);
  const gallery = useMemo(() => (pkg ? packageGallery(pkg) : []), [pkg]);
  const itinerary = useMemo(() => (pkg ? packageItinerary(pkg) : []), [pkg]);
  const sections = useMemo(
    () =>
      (pkg ? packageSections(pkg) : [])
        .map((s) => ({ ...s, html: sanitizeHtml(s.body) }))
        .filter((s) => s.html),
    [pkg],
  );
  const description = useMemo(() => sanitizeHtml(pkg?.description), [pkg]);
  const terms = useMemo(() => sanitizeHtml(pkg?.termsText), [pkg]);
  const reviews = useMemo(() => (pkg ? packageReviews(pkg) : null), [pkg]);
  const videos = useMemo(() => (pkg ? packageVideos(pkg) : []), [pkg]);
  const option = options.find((o) => o.priceKey === selection.priceKey) || null;
  const hotels = useMemo(
    () => (pkg ? hotelsForGrade(pkg, option?.grade || null) : []),
    [pkg, option],
  );
  const flights = useMemo(
    () => (pkg ? flightsForCity(pkg, option?.city) : []),
    [pkg, option],
  );
  const estimate = estimatePackageTotal(option, selection.pax);
  const currentPaxError = paxError(selection.pax);

  // sections this package has, in page order — drives the nav
  const nav = useMemo(
    () =>
      pkg
        ? NAV.filter((n) => {
            if (n.key === 'overview')
              return Boolean(description || pkg.summary);
            if (n.key === 'itinerary') return itinerary.length > 0;
            if (n.key === 'stay') return (pkg.hotels || []).length > 0;
            if (n.key === 'flights') return (pkg.flights || []).length > 0;
            if (n.key === 'inclusions') return sections.length > 0;
            if (n.key === 'videos') return videos.length > 0;
            if (n.key === 'reviews') return Boolean(reviews);
            return true;
          })
        : [],
    [pkg, description, itinerary, sections, videos, reviews],
  );
  const [activeSection, setActiveSection] = useActiveSection(
    nav.map((n) => n.key),
  );

  // first bookable departure and first price option are pre-picked
  useEffect(() => {
    if (!pkg) return;
    setSelection((s) => {
      const dep = departures.find((d) => d.key === s.departureKey && d.bookable)
        ? s.departureKey
        : departures.find((d) => d.bookable)?.key || '';
      const price = options.some((o) => o.priceKey === s.priceKey)
        ? s.priceKey
        : options[0]?.priceKey || '';
      // the searched room sharing may include types this option doesn't sell
      const pax = paxForOption(
        s.pax,
        options.find((o) => o.priceKey === price),
      );
      return dep === s.departureKey && price === s.priceKey && pax === s.pax
        ? s
        : { ...s, departureKey: dep, priceKey: price, pax };
    });
  }, [pkg, departures, options]);

  const changeSelection = useCallback(
    (patch) => {
      setQuoteError('');
      setSelection((s) => {
        const next = { ...s, ...patch };
        // a price option that does not sell a sharing type drops those people
        if (patch.priceKey) {
          next.pax = paxForOption(
            next.pax,
            options.find((o) => o.priceKey === patch.priceKey),
          );
        }
        return next;
      });
    },
    [options],
  );

  const backToResults = () =>
    navigate(
      search.destination
        ? `${PACKAGE_ROUTES.RESULTS}?${buildPackageSearchQuery(search)}`
        : PACKAGE_ROUTES.LANDING,
    );

  const scrollTo = (key) => {
    setActiveSection(key);
    document
      .getElementById(`pkg-section-${key}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleContinue = async () => {
    if (!pkg || quoting || !option || currentPaxError) return;
    const departure = departures.find((d) => d.key === selection.departureKey);
    if (!departure?.bookable) return;
    setQuoting(true);
    setQuoteError('');
    const read = await packageQuoteApi({
      packageKey: pkg.packageKey || packageKey,
      priceKey: option.priceKey,
      departureKey: departure.key,
      pax: apiPax(selection.pax),
      searchId,
    });
    setQuoting(false);
    const quote = read.ok ? read.data?.package : null;
    if (!quote?.nextraQuoteId) {
      let message = read.message || 'Could not get a price for this selection.';
      if (read.network)
        message = 'We could not reach the server. Please try again.';
      setQuoteError(message);
      return;
    }
    const context = {
      packageKey: pkg.packageKey || packageKey,
      searchId,
      departureKey: departure.key,
      priceKey: option.priceKey,
      pax: normalizePax(selection.pax),
      search,
      quote,
      quotedAt: Date.now(),
      summary: {
        name: pkg.name,
        destination: packagePlace(pkg),
        days: packageDays(pkg),
        nights: packageNights(pkg),
        cover: packageImageUrl(pkg.coverImage) || gallery[0]?.url || '',
        supplier: packageSupplier(pkg),
        departureDate: toIsoDate(departure.date),
        returnDate: departure.returnDate ? toIsoDate(departure.returnDate) : '',
        city: option.city,
        grade: gradeLabel(option.grade, pkg),
        depositPct: Number(pkg.depositPct) || 0,
        balanceDueDaysBeforeTravel: Number(pkg.balanceDueDaysBeforeTravel) || 0,
      },
    };
    savePackageBookContext(context);
    navigate(PACKAGE_ROUTES.BOOKING, { state: context });
  };

  const summaryBar = (
    <PackageSummaryBar
      onBack={backToResults}
      backLabel="Back to results"
      items={[
        {
          icon: <FiMapPin />,
          label: 'Destination',
          value: pkg
            ? packagePlace(pkg)
            : prettyDestination(search.destination),
        },
        pkg && {
          icon: <FiMoon />,
          label: 'Duration',
          value: daysNightsText(packageDays(pkg), packageNights(pkg)),
        },
        {
          icon: <FiUsers />,
          label: 'Travellers',
          value: paxSummary(selection.pax),
        },
      ]}
    />
  );

  if (!packageKey) {
    return (
      <div className="pkg-details">
        <Container className="pkg-details__main">
          <PackageState
            title="Package not found"
            message="The link is missing the package. Search again to pick one."
            primaryText="Search packages"
            onPrimary={() => navigate(PACKAGE_ROUTES.LANDING)}
          />
        </Container>
      </div>
    );
  }

  if (state.loading) {
    return (
      <div className="pkg-details">
        {summaryBar}
        <DetailsSkeleton />
      </div>
    );
  }

  if (!pkg) {
    const { read } = state;
    return (
      <div className="pkg-details">
        {summaryBar}
        <Container className="pkg-details__main">
          <PackageState
            title={
              read?.network
                ? 'We could not reach the server'
                : 'Package details are not available'
            }
            message={
              read?.network
                ? 'Check your connection and try again.'
                : read?.message || 'Please go back and try another package.'
            }
            reference={read?.correlationId}
            primaryText="Try again"
            onPrimary={() => setAttempt((n) => n + 1)}
            secondaryText="Back to results"
            onSecondary={backToResults}
          />
        </Container>
      </div>
    );
  }

  const days = packageDays(pkg);
  const nights = packageNights(pkg);
  const supplier = packageSupplier(pkg);
  const ratingCount = Number(pkg.reviews?.summary?.ratingCount) || 0;
  const ratingAvg = Number(pkg.reviews?.summary?.ratingAvg) || 0;
  const groupMin = Number(pkg.groupSize?.min) || 0;
  const groupMax = Number(pkg.groupSize?.max) || 0;
  const grades = Array.isArray(pkg.grades) ? pkg.grades.filter(Boolean) : [];
  const bookableDepartures = departures.filter((d) => d.bookable);
  const nextDeparture = bookableDepartures[0] || null;
  const fromPrice = Number(pkg.fare?.grandTotal) || null;
  const currency = pkg.fare?.currency || 'INR';
  const hotelCount = (pkg.hotels || []).length;
  const flightCount = (pkg.flights || []).length;
  const mealCount = itinerary.reduce(
    (n, d) =>
      n +
      ['breakfast', 'lunch', 'dinner'].filter((m) => Number(d[m]) > 0).length,
    0,
  );

  // "At a glance" — only what this package's data actually says
  const glance = [
    hotelCount > 0 && {
      key: 'stay',
      icon: <MdOutlineHotel />,
      text: `${hotelCount} hotel option${hotelCount === 1 ? '' : 's'}`,
    },
    flightCount > 0 && {
      key: 'flights',
      icon: <FiSend />,
      text: 'Flights listed',
    },
    mealCount > 0 && {
      key: 'meals',
      icon: <FiCoffee />,
      text: `${mealCount} meal${mealCount === 1 ? '' : 's'} included`,
    },
    itinerary.length > 0 && {
      key: 'plan',
      icon: <FiMap />,
      text: `${itinerary.length}-day plan`,
    },
    departures.some((d) => d.bookable && d.guaranteed) && {
      key: 'guaranteed',
      icon: <FiCheckCircle />,
      text: 'Guaranteed departures',
    },
  ].filter(Boolean);

  return (
    <div className="pkg-details">
      {summaryBar}

      <Container className="pkg-details__main">
        <PackageGallery images={gallery} title={pkg.name} />

        <div className="pkg-details__grid">
          <div className="pkg-details__content">
            <div className="pkg-intro">
              <div className="pkg-intro__top">
                {supplier && (
                  <span className="pkg-intro__supplier">
                    <FiBriefcase /> {supplier}
                  </span>
                )}
                {ratingCount > 0 && (
                  <button
                    type="button"
                    className="pkg-intro__rating"
                    onClick={() => scrollTo('reviews')}
                  >
                    <FiStar /> {ratingAvg.toFixed(1)} · {ratingCount} review
                    {ratingCount === 1 ? '' : 's'}
                  </button>
                )}
              </div>
              <h1 className="pkg-intro__title">{pkg.name}</h1>
              {packagePlace(pkg) && (
                <p className="pkg-intro__place">
                  <FiMapPin /> {packagePlace(pkg)}
                </p>
              )}

              <div className="pkg-facts">
                {days != null && (
                  <Fact
                    icon={<FiMoon />}
                    label="Duration"
                    value={daysNightsText(days, nights)}
                  />
                )}
                <Fact
                  icon={<FiCalendar />}
                  label="Next departure"
                  value={nextDeparture ? formatDate(nextDeparture.date) : '—'}
                  sub={
                    bookableDepartures.length > 1
                      ? `+${bookableDepartures.length - 1} more date${
                          bookableDepartures.length - 1 === 1 ? '' : 's'
                        }`
                      : ''
                  }
                />
                {(groupMin > 0 || groupMax > 0) && (
                  <Fact
                    icon={<FiUsers />}
                    label="Group size"
                    value={`${groupMin || 1} - ${groupMax || groupMin} people`}
                  />
                )}
                <Fact
                  accent
                  icon={<FiTag />}
                  label="Starting from"
                  value={fromPrice ? formatPrice(fromPrice, currency) : '—'}
                  sub="per person"
                />
              </div>

              {glance.length > 0 && (
                <div className="pkg-glance">
                  <span className="pkg-glance__label">At a glance</span>
                  <ul>
                    {glance.map((g) => (
                      <li key={g.key}>
                        {g.icon}
                        {g.text}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {grades.length > 1 && (
                <div className="pkg-glance">
                  <span className="pkg-glance__label">Hotel grades</span>
                  <div className="pkg-intro__grades">
                    {grades.map((g) => (
                      <span
                        key={g}
                        className={`pkg-chip ${
                          option && gradeLabel(option.grade, pkg) === g
                            ? 'pkg-chip--dark'
                            : ''
                        }`}
                      >
                        {g}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <nav className="pkg-nav" aria-label="Package sections">
              {nav.map((n) => (
                <button
                  type="button"
                  key={n.key}
                  className={activeSection === n.key ? 'is-active' : ''}
                  aria-current={activeSection === n.key ? 'true' : undefined}
                  onClick={() => scrollTo(n.key)}
                >
                  {n.label}
                </button>
              ))}
            </nav>

            {(description || pkg.summary) && (
              <Section id="overview" title="Overview">
                {pkg.summary && description && (
                  <p className="pkg-lead">{pkg.summary.trim()}</p>
                )}
                {description ? (
                  <div
                    className="pkg-html"
                    dangerouslySetInnerHTML={{ __html: description }}
                  />
                ) : (
                  <p className="pkg-html">{pkg.summary}</p>
                )}
              </Section>
            )}

            {itinerary.length > 0 && (
              <Section id="itinerary" title="Day-wise Itinerary">
                <PackageItinerary days={itinerary} />
              </Section>
            )}

            {hotelCount > 0 && (
              <Section
                id="stay"
                title="Hotels"
                aside={
                  option && (
                    <span className="pkg-section__for">
                      For {gradeLabel(option.grade, pkg)}
                    </span>
                  )
                }
              >
                <PackageHotels hotels={hotels} />
              </Section>
            )}

            {flightCount > 0 && (
              <Section
                id="flights"
                title="Flights"
                aside={
                  option?.city && (
                    <span className="pkg-section__for">From {option.city}</span>
                  )
                }
              >
                <PackageFlights flights={flights} />
              </Section>
            )}

            {sections.length > 0 && (
              <Section id="inclusions" title="Inclusions & Information">
                <div className="pkg-terms">
                  {sections.map((sec) => {
                    const tone = sectionTone(sec.title);
                    const ToneIcon = TONE_ICON[tone];
                    return (
                      <div
                        key={sec.title}
                        className={`pkg-terms__block pkg-terms__block--${tone}`}
                      >
                        <h3>
                          <ToneIcon /> {sec.title}
                        </h3>
                        <div
                          className="pkg-html"
                          dangerouslySetInnerHTML={{ __html: sec.html }}
                        />
                      </div>
                    );
                  })}
                </div>
              </Section>
            )}

            {videos.length > 0 && (
              <Section id="videos" title="Videos">
                <PackageVideos videos={videos} />
              </Section>
            )}

            {reviews && (
              <Section id="reviews" title="Traveller Reviews">
                <PackageReviews reviews={reviews} />
              </Section>
            )}

            <Section id="policies" title="Booking Policies">
              <ul className="pkg-policies">
                {Number(pkg.depositPct) > 0 && (
                  <li className="pkg-policies__item pkg-policies__item--key">
                    <FiInfo />
                    <span>
                      A {Number(pkg.depositPct)}% deposit secures the booking;
                      the balance is due{' '}
                      {Number(pkg.balanceDueDaysBeforeTravel) > 0
                        ? `${pkg.balanceDueDaysBeforeTravel} days before departure`
                        : 'before departure'}
                      .
                    </span>
                  </li>
                )}
                <li className="pkg-policies__item">
                  <FiShield />
                  <span>
                    Pay from your agency wallet or online by card, net banking
                    or UPI when you book.
                  </span>
                </li>
                <li className="pkg-policies__item">
                  <FiTag />
                  <span>
                    Prices are per person and are confirmed when you continue to
                    book; the estimate can change until then.
                  </span>
                </li>
              </ul>
              {terms && (
                <div
                  className="pkg-html pkg-policies__terms"
                  dangerouslySetInnerHTML={{ __html: terms }}
                />
              )}
            </Section>
          </div>

          <aside className="pkg-details__aside">
            <PackageBookingPanel
              pkg={pkg}
              departures={departures}
              options={options}
              selection={selection}
              onChange={changeSelection}
              estimate={estimate}
              paxError={currentPaxError}
              quoting={quoting}
              quoteError={quoteError}
              onContinue={handleContinue}
              onEnquire={() => setEnquiryOpen(true)}
            />
          </aside>
        </div>
      </Container>

      {/* phones: the panel sits below the content, so keep a way to it in reach */}
      <div className="pkg-details__mobilebar">
        <div>
          <small>Starting from</small>
          <strong>{fromPrice ? formatPrice(fromPrice, currency) : '—'}</strong>
        </div>
        <div className="pkg-details__mobilebar-actions">
          <button
            type="button"
            className="pkg-state__secondary"
            onClick={() => setEnquiryOpen(true)}
            aria-label="Send an enquiry"
          >
            <FiMessageSquare />
          </button>
          <button
            type="button"
            className="pkg-state__primary"
            onClick={() =>
              document
                .getElementById('pkg-panel')
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
          >
            <FiCalendar /> Select dates
          </button>
        </div>
      </div>

      <PackageEnquiryModal
        show={enquiryOpen}
        onHide={() => setEnquiryOpen(false)}
        packageKey={pkg.packageKey || packageKey}
        searchId={searchId}
        packageName={pkg.name}
      />
    </div>
  );
}

export default PackageDetailsPage;
